import React, { useEffect, useState } from 'react'
import { 
  Box, 
  Container, 
  Grid, 
  Paper, 
  Typography, 
  Button,
  Card,
  CardContent,
  CardActions,
  LinearProgress,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  CircularProgress,
  Fab,
  Alert,
  Snackbar
} from '@mui/material'
import { styled } from '@mui/material/styles'
import AddIcon from '@mui/icons-material/Add'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import LocalGroceryStoreIcon from '@mui/icons-material/LocalGroceryStore'
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks'
import BarChartIcon from '@mui/icons-material/BarChart'
import RestaurantIcon from '@mui/icons-material/Restaurant'
import { makeGetRequest, makePostRequest, makeDeleteRequest } from '../../utils/api'
import urls from '../../utils/urls'
import ShoppingListDetail from './ShoppingListDetail'
import ItemLibrary from './ItemLibrary'
import CreateListDialog from './CreateListDialog'
import CreateFromRecipesDialog from './CreateFromRecipesDialog'
import Statistics from './Statistics'
import { useTranslation } from 'react-i18next' // LAP-733 (GE022)

// Styled components for dark theme
const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  background: 'linear-gradient(145deg, #1e1e2e 0%, #252540 100%)',
  boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
  transition: 'all 0.3s ease',
  border: '1px solid rgba(255,255,255,0.05)',
  '&:hover': {
    boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
    border: '1px solid rgba(255,255,255,0.1)',
    transform: 'translateY(-4px)',
  },
}))

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: 20,
  textTransform: 'none',
  fontWeight: 600,
  padding: '12px 28px',
  boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
  transition: 'all 0.3s ease',
}))

const Shopping = () => {
  const { t } = useTranslation('layout') // LAP-733 (GE022)
  const [lists, setLists] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedList, setSelectedList] = useState(null)
  const [showLibrary, setShowLibrary] = useState(false)
  const [showStatistics, setShowStatistics] = useState(false)
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [showCreateFromRecipes, setShowCreateFromRecipes] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)
  const [selectedListForMenu, setSelectedListForMenu] = useState(null)
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' })

  useEffect(() => {
    fetchLists()
  }, [])

  const fetchLists = async () => {
    setLoading(true)
    try {
      const response = await makeGetRequest(urls.shoppingLists, {}, true)
      if (response?.status === 200) {
        setLists(response.data.results || [])
      }
    } catch (error) {
      console.error('Error fetching lists:', error)
      /* LAP-733 (GE022) */ showSnackbar(t('shopping.errLoad'), 'error')
    } finally {
      setLoading(false)
    }
  }

  const handleCreateList = async (listData) => {
    try {
      const response = await makePostRequest(urls.shoppingLists, listData, {}, true)
      if (response?.status === 201) {
        await fetchLists()
        setShowCreateDialog(false)
        /* LAP-733 (GE022) */ showSnackbar(t('shopping.msgCreated'), 'success')
        // Open the newly created list
        setSelectedList(response.data)
      }
    } catch (error) {
      console.error('Error creating list:', error)
      /* LAP-733 (GE022) */ showSnackbar(t('shopping.errCreate'), 'error')
    }
  }

  const handleCreateFromRecipes = (listData) => {
    // List data already created by the dialog
    setShowCreateFromRecipes(false)
    fetchLists()
    // Open the newly created list
    setSelectedList(listData)
  }

  const handleDeleteList = async (listId) => {
    if (/* LAP-733 (GE022) */ window.confirm(t('shopping.confirmDelete'))) {
      try {
        const response = await makeDeleteRequest(`${urls.shoppingLists}${listId}/`, {}, true)
        if (response?.status === 204) {
          await fetchLists()
          /* LAP-733 (GE022) */ showSnackbar(t('shopping.msgDeleted'), 'success')
          if (selectedList?.id === listId) {
            setSelectedList(null)
          }
        }
      } catch (error) {
        console.error('Error deleting list:', error)
        /* LAP-733 (GE022) */ showSnackbar(t('shopping.errDelete'), 'error')
      }
    }
  }

  const handleCopyList = async (listId) => {
    try {
      const response = await makePostRequest(`${urls.shoppingLists}${listId}/copy/`, {}, {}, true)
      if (response?.status === 201) {
        await fetchLists()
        /* LAP-733 (GE022) */ showSnackbar(t('shopping.msgCopied'), 'success')
      }
    } catch (error) {
      console.error('Error copying list:', error)
      /* LAP-733 (GE022) */ showSnackbar(t('shopping.errCopy'), 'error')
    }
  }

  const handleMenuOpen = (event, list) => {
    setAnchorEl(event.currentTarget)
    setSelectedListForMenu(list)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
    setSelectedListForMenu(null)
  }

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity })
  }

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false })
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'to_do':
        return 'default'
      case 'in_progress':
        return 'primary'
      case 'completed':
        return 'success'
      default:
        return 'default'
    }
  }

  const getStatusLabel = (status) => {
    switch (status) {
      case 'to_do':
        // LAP-733 (GE022)
        return t('shopping.statusToDo')
      case 'in_progress':
        // LAP-733 (GE022)
        return t('shopping.statusInProgress')
      case 'completed':
        // LAP-733 (GE022)
        return t('shopping.statusCompleted')
      default:
        return status
    }
  }

  // If viewing a specific list
  if (selectedList) {
    return (
      <ShoppingListDetail
        list={selectedList}
        onBack={() => {
          setSelectedList(null)
          fetchLists()
        }}
        onUpdate={fetchLists}
        showSnackbar={showSnackbar}
      />
    )
  }

  // If viewing library
  if (showLibrary) {
    return (
      <ItemLibrary
        onBack={() => setShowLibrary(false)}
        showSnackbar={showSnackbar}
      />
    )
  }

  // If viewing statistics
  if (showStatistics) {
    return (
      <Statistics
        onBack={() => setShowStatistics(false)}
      />
    )
  }

  // Main list view
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#0f0f1e', pb: 4 }}>
      <Container maxWidth="lg" sx={{ pt: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom sx={{ color: 'white', fontWeight: 700 }}>
            <ShoppingCartIcon sx={{ fontSize: 40, verticalAlign: 'middle', mr: 2, color: '#667eea' }} />
            {/* LAP-733 (GE022) */}
            {t('shopping.pageTitle')}
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.6)' }}>
            {/* LAP-733 (GE022) */}
            {t('shopping.pageSubtitle')}
          </Typography>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ mb: 4, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <ActionButton
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowCreateDialog(true)}
            sx={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              '&:hover': {
                background: 'linear-gradient(135deg, #7e8ef5 0%, #8b5db8 100%)',
              },
            }}
          >
            {/* LAP-733 (GE022) */}
            {t('shopping.btnNewList')}
          </ActionButton>
          <ActionButton
            variant="contained"
            startIcon={<RestaurantIcon />}
            onClick={() => setShowCreateFromRecipes(true)}
            sx={{ 
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              color: 'white',
              '&:hover': {
                background: 'linear-gradient(135deg, #f5a4ff 0%, #ff6b7f 100%)',
              },
            }}
          >
            {/* LAP-733 (GE022) */}
            {t('shopping.btnFromRecipes')}
          </ActionButton>
          <ActionButton
            variant="outlined"
            startIcon={<LibraryBooksIcon />}
            onClick={() => setShowLibrary(true)}
            sx={{ 
              borderColor: 'rgba(102, 126, 234, 0.5)',
              color: '#667eea',
              '&:hover': {
                borderColor: '#667eea',
                bgcolor: 'rgba(102, 126, 234, 0.1)',
              },
            }}
          >
            {/* LAP-733 (GE022) */}
            {t('shopping.btnLibrary')}
          </ActionButton>
          <ActionButton
            variant="outlined"
            startIcon={<BarChartIcon />}
            onClick={() => setShowStatistics(true)}
            sx={{ 
              borderColor: 'rgba(102, 126, 234, 0.5)',
              color: '#667eea',
              '&:hover': {
                borderColor: '#667eea',
                bgcolor: 'rgba(102, 126, 234, 0.1)',
              },
            }}
          >
            {/* LAP-733 (GE022) */}
            {t('shopping.btnStats')}
          </ActionButton>
        </Box>

        {/* Loading State */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress sx={{ color: '#667eea' }} />
          </Box>
        )}

        {/* Empty State */}
        {!loading && lists.length === 0 && (
          <StyledCard sx={{ p: 6, textAlign: 'center' }}>
            <LocalGroceryStoreIcon sx={{ fontSize: 80, color: 'rgba(255,255,255,0.3)', mb: 2 }} />
            <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
              {/* LAP-733 (GE022) */}
              {t('shopping.emptyTitle')}
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mb: 3 }}>
              {/* LAP-733 (GE022) */}
              {t('shopping.emptyHint')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <ActionButton
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setShowCreateDialog(true)}
                sx={{ 
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                }}
              >
                {/* LAP-733 (GE022) */}
                {t('shopping.btnCreateFirst')}
              </ActionButton>
              <ActionButton
                variant="contained"
                startIcon={<RestaurantIcon />}
                onClick={() => setShowCreateFromRecipes(true)}
                sx={{ 
                  background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                  color: 'white',
                }}
              >
                {/* LAP-733 (GE022) */}
                {t('shopping.btnFromRecipes')}
              </ActionButton>
            </Box>
          </StyledCard>
        )}

        {/* Lists Grid */}
        {!loading && lists.length > 0 && (
          <Grid container spacing={3}>
            {lists.map((list) => (
              <Grid item xs={12} sm={6} md={4} key={list.id}>
                <StyledCard 
                  sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: 'pointer',
                  }}
                >
                  <CardContent 
                    sx={{ flexGrow: 1 }}
                    onClick={() => setSelectedList(list)}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="h6" component="div" noWrap sx={{ color: 'white', fontWeight: 600 }}>
                        {list.name}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleMenuOpen(e, list)
                        }}
                        sx={{ color: 'rgba(255,255,255,0.7)' }}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Chip 
                        label={getStatusLabel(list.status)} 
                        size="small"
                        sx={{
                          bgcolor: getStatusColor(list.status) === 'success' ? 'rgba(76, 175, 80, 0.2)' :
                                   getStatusColor(list.status) === 'primary' ? 'rgba(102, 126, 234, 0.2)' :
                                   'rgba(255, 255, 255, 0.1)',
                          color: getStatusColor(list.status) === 'success' ? '#4caf50' :
                                 getStatusColor(list.status) === 'primary' ? '#667eea' :
                                 'rgba(255,255,255,0.7)',
                          border: '1px solid rgba(255,255,255,0.1)',
                          fontWeight: 600,
                        }}
                      />
                    </Box>

                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }} gutterBottom>
                      {/* LAP-733 (GE022) */}
                      {t('shopping.itemsAndBought', { count: list.total_items, bought: list.bought_items })}
                    </Typography>

                    <Box sx={{ mt: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                          {/* LAP-733 (GE022) */}
                          {t('shopping.progression')}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#667eea', fontWeight: 600 }}>
                          {list.progress.toFixed(0)}%
                        </Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={list.progress} 
                        sx={{ 
                          height: 8, 
                          borderRadius: 4,
                          bgcolor: 'rgba(255,255,255,0.1)',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: '#4caf50',
                            borderRadius: 4,
                          },
                        }}
                      />
                    </Box>

                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', mt: 2, display: 'block' }}>
                      {/* LAP-733 (GE022) */}
                      {t('shopping.createdOn', { date: new Date(list.created_at).toLocaleDateString() })}
                    </Typography>
                  </CardContent>

                  <CardActions sx={{ p: 2, pt: 0 }}>
                    <Button 
                      size="small" 
                      onClick={() => setSelectedList(list)}
                      sx={{ 
                        color: '#667eea',
                        fontWeight: 600,
                        '&:hover': {
                          bgcolor: 'rgba(102, 126, 234, 0.1)',
                        },
                      }}
                    >
                      {/* LAP-733 (GE022) */}
                      {t('shopping.btnViewDetails')}
                    </Button>
                  </CardActions>
                </StyledCard>
              </Grid>
            ))}
          </Grid>
        )}

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          setSelectedList(selectedListForMenu)
          handleMenuClose()
        }}>
          {/* LAP-733 (GE022) */}
          {t('shopping.menuOpen')}
        </MenuItem>
        <MenuItem onClick={() => {
          handleCopyList(selectedListForMenu.id)
          handleMenuClose()
        }}>
          {/* LAP-733 (GE022) */}
          {t('shopping.menuCopy')}
        </MenuItem>
        <MenuItem onClick={() => {
          handleDeleteList(selectedListForMenu.id)
          handleMenuClose()
        }}>
          {/* LAP-733 (GE022) */}
          {t('shopping.menuDelete')}
        </MenuItem>
      </Menu>

      {/* Create List Dialog */}
      <CreateListDialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        onCreate={handleCreateList}
      />

      {/* Create From Recipes Dialog */}
      <CreateFromRecipesDialog
        open={showCreateFromRecipes}
        onClose={() => setShowCreateFromRecipes(false)}
        onCreate={handleCreateFromRecipes}
        showSnackbar={showSnackbar}
      />

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* FAB for quick create */}
        <Fab
          aria-label="add"
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)',
            '&:hover': {
              background: 'linear-gradient(135deg, #7e8ef5 0%, #8b5db8 100%)',
              boxShadow: '0 12px 32px rgba(102, 126, 234, 0.5)',
            },
          }}
          onClick={() => setShowCreateDialog(true)}
        >
          <AddIcon />
        </Fab>
      </Container>
    </Box>
  )
}

export default Shopping
